import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { environment as env } from 'src/environments/environment';
import { PrivacyModel } from 'src/app/interfaces/privacy-model.interface';
import { ApiService } from '../api/api.service';
import { ToastService } from '@msi/cobalt';
import {
	AcsEventMessage,
	ActivateOrchestrate,
	SelfRegistrationStatus,
} from 'src/app/interfaces/self-registration-models.interface';
import { share } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { notNull } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';

@Injectable({
	providedIn: 'root',
})
export class SelfRegistrationService {
	messageListener: (event: { data: AcsEventMessage }) => void;
	activateOrchestrateBody: ActivateOrchestrate;

	private statusSubject: BehaviorSubject<SelfRegistrationStatus> = new BehaviorSubject<SelfRegistrationStatus>(null);
	public get status$(): Observable<SelfRegistrationStatus> {
		return this.statusSubject.asObservable().pipe(notNull());
	}

	public get token() {
		return this._token;
	}

	public get agreements() {
		return this._agreements;
	}

	private _agreements: PrivacyModel[];
	private _token: string;

	constructor(
		private apiService: ApiService,
		private toastService: ToastService
	) {}

	attachMessageListener() {
		this.messageListener = (event: { data: AcsEventMessage }) => {
			// Return as if it is not in mfe
			if (
				event.data?.type !== 'selfRegistrationMessage' ||
				event.data?.token === null ||
				this._token === event.data?.token
			)
				return;
			// Cache token
			this._token = event.data.token;

			this.activateOrchestrateBody = { ...event.data };

			// Checks if the user agency is registered in ACS connector
			forkJoin([
				this.apiService.getSelfRegStatus(event.data.token, this.activateOrchestrateBody.source),
				// this.apiService.getAgreementsACS(event.data.token, this.activateOrchestrateBody.source),
			]).subscribe({
				next: ([selfRegStatusResponse]) => {
					// this._agreements = agreements.map(agreement => {
					// 	return {
					// 		name: agreement.name,
					// 		url: agreement.url,
					// 		checked: false,
					// 		agreementId: agreement.id,
					// 		acceptedAt: null,
					// 	};
					// });
					this.statusSubject.next(selfRegStatusResponse);
				},
				error: () => {
					this.toastService.error($localize`Error checking registration. Please reload the page.`);
				},
			});
			window.removeEventListener('message', this.messageListener);
		};
		if (this.inIframe()) {
			window.addEventListener('message', this.messageListener);
		}
	}

	/**
	 * Redirects the user to Orchestrate based on the current environment set
	 */
	redirectToOrchestrate() {
		window.open(`https://${env.clientHost}`, '_blank');
	}

	acceptButtonClicked() {
		const request = this.apiService.activateOrchestrate(this._token, this.activateOrchestrateBody).pipe(share());
		request.subscribe({
			next: () => {
				// this.updateStatusAfterLinking();
			},
			error: (err: HttpErrorResponse) => {
				if (err.status === 400) {
					this.toastService.error(err.message);
				} else if (err.status === 412) {
					this.updateStatusAfterLinking();
				} else {
					this.toastService.error($localize`Error while requesting Orchestrate, please try again later.`, null, {
						autoDismiss: 10000,
					});
				}
			},
		});
		return request;
	}

	private updateStatusAfterLinking() {
		const status = this.statusSubject.value;
		status.hasOrchestrate = true;
		status.isLinked = status.hasCentralStation;
		this.statusSubject.next(status);
	}

	/**
	 * @returns true if the window is in an iframe false if not
	 */
	private inIframe(): boolean {
		try {
			return window.self !== window.top;
		} catch (e) {
			return true;
		}
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
